/**
 * Canvas2D artwork for everything drawn as an atlas sprite: tree and peak glyphs, city markers,
 * battle and courier symbols, particle sprites, tag digits. Drawn once at 4–5× their typical
 * on-screen size so mipmapping gives clean results from whole-map zoom up to 4×.
 */
import type { Atlas, Frame } from '../gfx/atlas';
import { cssColor, PAL } from '../palette';

/** A frame plus its size in layout units (what QuadBatch.push expects). */
export interface Glyph {
  frame: Frame;
  w: number;
  h: number;
}

const glyph = (
  atlas: Atlas,
  key: string,
  w: number,
  h: number,
  px: number,
  draw: (c: CanvasRenderingContext2D) => void,
): Glyph => {
  const frame = atlas.get(key, w * px, h * px, (c) => {
    c.scale(px, px);
    draw(c);
  });
  return { frame, w, h };
};

let seed = 1;
const rnd = () => {
  seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
  return seed / 4294967296;
};

// ---------------------------------------------------------------------------------------------
// Terrain glyphs (world units)
// ---------------------------------------------------------------------------------------------

const TREE_INK = '#56603f';
const TREE_FILL = '#98a676';
const TREE_LIT = '#b7c291';

/** Deciduous (round crown) and conifer tree glyphs, 12 variants. */
export function treeGlyphs(atlas: Atlas): Glyph[] {
  const out: Glyph[] = [];
  for (let v = 0; v < 12; v++) {
    seed = 1000 + v * 17;
    const conifer = v >= 8;
    out.push(
      glyph(atlas, `tree${v}`, 12, 14, 5, (c) => {
        c.lineJoin = 'round';
        c.lineCap = 'round';
        // cast shadow
        c.fillStyle = 'rgba(60,55,35,0.22)';
        c.beginPath();
        c.ellipse(7.4, 12.3, 3.8, 1.2, 0, 0, Math.PI * 2);
        c.fill();
        c.strokeStyle = TREE_INK;
        c.lineWidth = 0.55;
        // trunk
        c.beginPath();
        c.moveTo(6, 12.6);
        c.lineTo(6, 9);
        c.stroke();
        if (conifer) {
          const hgt = 8.5 + rnd() * 2;
          c.beginPath();
          c.moveTo(6, 12 - hgt);
          const tiers = 3;
          for (let t = 1; t <= tiers; t++) c.lineTo(6 + (1.2 + t * 1.05), 12 - hgt + (hgt * t) / tiers - 0.6);
          for (let t = tiers; t >= 1; t--) c.lineTo(6 - (1.2 + t * 1.05), 12 - hgt + (hgt * t) / tiers - 0.6);
          c.closePath();
          c.fillStyle = TREE_FILL;
          c.fill();
          c.stroke();
          c.strokeStyle = 'rgba(86,96,63,0.6)';
          c.beginPath();
          c.moveTo(6, 12 - hgt + 1.2);
          c.lineTo(6, 10.6);
          c.stroke();
        } else {
          const r = 2.6 + rnd() * 0.7;
          const blobs: [number, number, number][] = [
            [6 - r * 0.62, 7.2 + rnd() * 0.4, r * 0.82],
            [6 + r * 0.62, 7.0 + rnd() * 0.4, r * 0.86],
            [6 + (rnd() - 0.5) * 0.6, 5.2 - rnd() * 0.5, r],
          ];
          c.beginPath();
          for (const [x, y, rr] of blobs) {
            c.moveTo(x + rr, y);
            c.arc(x, y, rr, 0, Math.PI * 2);
          }
          c.fillStyle = TREE_FILL;
          c.fill();
          // lit highlight on the upper left
          c.save();
          c.clip();
          c.fillStyle = TREE_LIT;
          c.beginPath();
          c.arc(4.6, 4.2, r * 0.9, 0, Math.PI * 2);
          c.fill();
          c.restore();
          // ink outline: stroke each blob but only its outer rim (draw, then refill interior)
          for (const [x, y, rr] of blobs) {
            c.beginPath();
            c.arc(x, y, rr, 0, Math.PI * 2);
            c.stroke();
          }
          c.save();
          c.beginPath();
          for (const [x, y, rr] of blobs) {
            c.moveTo(x + rr - 0.45, y);
            c.arc(x, y, rr - 0.45, 0, Math.PI * 2);
          }
          c.clip();
          c.fillStyle = TREE_FILL;
          c.fillRect(0, 0, 12, 14);
          c.fillStyle = TREE_LIT;
          c.beginPath();
          c.arc(4.6, 4.2, r * 0.9, 0, Math.PI * 2);
          c.fill();
          // stipple
          c.fillStyle = 'rgba(86,96,63,0.55)';
          for (let i = 0; i < 7; i++) {
            c.beginPath();
            c.arc(4 + rnd() * 5.5, 4.5 + rnd() * 4.5, 0.28, 0, Math.PI * 2);
            c.fill();
          }
          c.restore();
        }
      }),
    );
  }
  return out;
}

const PEAK_INK = '#5a4935';

/** Mountain peaks: lit face in pale stone, shadow face hatched. 6 variants. */
export function peakGlyphs(atlas: Atlas): Glyph[] {
  const out: Glyph[] = [];
  for (let v = 0; v < 6; v++) {
    seed = 5000 + v * 31;
    out.push(
      glyph(atlas, `peak${v}`, 26, 18, 5, (c) => {
        c.lineJoin = 'round';
        c.lineCap = 'round';
        const peaks = v < 3 ? 1 : 2;
        const base = 16.5;
        const draw = (cx: number, hgt: number, half: number) => {
          const tipX = cx + (rnd() - 0.5) * 1.5;
          const tipY = base - hgt;
          const lx = cx - half;
          const rx = cx + half;
          // shadow face (right), then lit face (left)
          c.beginPath();
          c.moveTo(tipX, tipY);
          c.lineTo(rx, base);
          c.lineTo(tipX + half * 0.18, base);
          c.closePath();
          c.fillStyle = '#b5a381';
          c.fill();
          c.save();
          c.clip();
          c.strokeStyle = 'rgba(90,73,53,0.75)';
          c.lineWidth = 0.35;
          for (let i = -12; i < 30; i += 1.15) {
            c.beginPath();
            c.moveTo(tipX + i, tipY);
            c.lineTo(tipX + i - 5, base + 1);
            c.stroke();
          }
          c.restore();
          c.beginPath();
          c.moveTo(tipX, tipY);
          c.lineTo(tipX + half * 0.18, base);
          c.lineTo(lx, base);
          c.closePath();
          c.fillStyle = '#efe8d8';
          c.fill();
          // outline with a slightly irregular ridge
          c.strokeStyle = PEAK_INK;
          c.lineWidth = 0.7;
          c.beginPath();
          c.moveTo(lx, base);
          c.lineTo(lx + (tipX - lx) * 0.45, base - hgt * 0.42 - rnd() * 0.8);
          c.lineTo(tipX, tipY);
          c.lineTo(tipX + (rx - tipX) * 0.5, base - hgt * 0.5 + rnd() * 0.8);
          c.lineTo(rx, base);
          c.stroke();
          // ridge line down the middle
          c.lineWidth = 0.45;
          c.beginPath();
          c.moveTo(tipX, tipY);
          c.lineTo(tipX + half * 0.18, base - 1);
          c.stroke();
        };
        if (peaks === 2) {
          draw(8.5, 9 + rnd() * 2, 6.5);
          draw(16.5, 12 + rnd() * 3, 8);
        } else {
          draw(13, 12 + rnd() * 3.5, 9 + rnd() * 2);
        }
      }),
    );
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Markers (layout units ≈ css px at reference zoom)
// ---------------------------------------------------------------------------------------------

const INK = cssColor(PAL.ink);
const PAPER = cssColor(PAL.paper);

export interface MarkerGlyphs {
  town: Glyph;
  city: Glyph;
  metropolis: Glyph;
  capital: Glyph;
  glow: Glyph;
  sabres: Glyph;
  rider: Glyph;
  motorcycle: Glyph;
  softDot: Glyph;
  flash: Glyph;
  ring: Glyph;
  tooth: Glyph;
  chevron: Glyph;
  selection: Glyph;
  inspect: Glyph;
  hover: Glyph;
}

export function markerGlyphs(atlas: Atlas): MarkerGlyphs {
  const town = glyph(atlas, 'm-town', 10, 10, 6, (c) => {
    c.beginPath();
    c.arc(5, 5, 3.4, 0, Math.PI * 2);
    c.fillStyle = PAPER;
    c.fill();
    c.beginPath();
    c.arc(5, 5, 2.3, 0, Math.PI * 2);
    c.fillStyle = INK;
    c.fill();
  });
  const city = glyph(atlas, 'm-city', 12, 12, 6, (c) => {
    c.fillStyle = PAPER;
    c.fillRect(1.6, 1.6, 8.8, 8.8);
    c.fillStyle = INK;
    c.fillRect(2.8, 2.8, 6.4, 6.4);
    c.fillStyle = PAPER;
    c.fillRect(4.6, 4.6, 2.8, 2.8);
  });
  const metropolis = glyph(atlas, 'm-metro', 16, 16, 6, (c) => {
    c.fillStyle = PAPER;
    c.fillRect(1.5, 1.5, 13, 13);
    c.fillStyle = INK;
    c.fillRect(2.6, 2.6, 10.8, 10.8);
    c.fillStyle = PAPER;
    c.fillRect(4.2, 4.2, 7.6, 7.6);
    c.fillStyle = INK;
    c.fillRect(5.4, 5.4, 5.2, 5.2);
  });
  const star = (c: CanvasRenderingContext2D, cx: number, cy: number, r: number) => {
    c.beginPath();
    for (let i = 0; i < 10; i++) {
      const a = -Math.PI / 2 + (i * Math.PI) / 5;
      const rr = i % 2 ? r * 0.42 : r;
      c.lineTo(cx + Math.cos(a) * rr, cy + Math.sin(a) * rr);
    }
    c.closePath();
  };
  const capital = glyph(atlas, 'm-capital', 20, 20, 6, (c) => {
    c.beginPath();
    c.arc(10, 10, 8.6, 0, Math.PI * 2);
    c.fillStyle = PAPER;
    c.fill();
    c.lineWidth = 1.3;
    c.strokeStyle = INK;
    c.beginPath();
    c.arc(10, 10, 7.6, 0, Math.PI * 2);
    c.stroke();
    star(c, 10, 10.4, 6);
    c.fillStyle = '#9e2b22';
    c.fill();
    c.lineWidth = 0.6;
    c.stroke();
  });
  const glow = glyph(atlas, 'm-glow', 32, 32, 3, (c) => {
    const g = c.createRadialGradient(16, 16, 0, 16, 16, 16);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.25, 'rgba(255,255,255,0.55)');
    g.addColorStop(0.6, 'rgba(255,255,255,0.14)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    c.fillStyle = g;
    c.fillRect(0, 0, 32, 32);
  });
  const sabres = glyph(atlas, 'm-sabres', 24, 24, 6, (c) => {
    c.beginPath();
    c.arc(12, 12, 11, 0, Math.PI * 2);
    c.fillStyle = '#f3ecdb';
    c.fill();
    c.lineWidth = 1.2;
    c.strokeStyle = INK;
    c.stroke();
    c.lineCap = 'round';
    const sabre = (flip: number) => {
      c.save();
      c.translate(12, 12);
      c.scale(flip, 1);
      c.rotate(-Math.PI / 4);
      // curved blade
      c.beginPath();
      c.moveTo(0, 8.2);
      c.quadraticCurveTo(1.6, 0, 0.2, -8.6);
      c.lineTo(-0.9, -7.2);
      c.quadraticCurveTo(0.2, 0, -1.1, 8.2);
      c.closePath();
      c.fillStyle = INK;
      c.fill();
      // guard and grip
      c.lineWidth = 1.3;
      c.beginPath();
      c.moveTo(-2.6, 6.6);
      c.lineTo(2.2, 6.6);
      c.stroke();
      c.lineWidth = 1.6;
      c.beginPath();
      c.moveTo(-0.5, 7);
      c.lineTo(-0.5, 9.4);
      c.stroke();
      c.restore();
    };
    sabre(1);
    sabre(-1);
  });
  const rider = glyph(atlas, 'm-rider', 18, 18, 6, (c) => {
    c.beginPath();
    c.moveTo(9, 0.8);
    c.lineTo(17.2, 9);
    c.lineTo(9, 17.2);
    c.lineTo(0.8, 9);
    c.closePath();
    c.fillStyle = '#f3ecdb';
    c.fill();
    c.lineWidth = 1.1;
    c.strokeStyle = INK;
    c.stroke();
    // horseshoe
    c.lineWidth = 1.7;
    c.lineCap = 'round';
    c.beginPath();
    c.arc(9, 8.3, 3.3, Math.PI * 0.95, Math.PI * 2.05, false);
    c.moveTo(5.8, 8.9);
    c.lineTo(6.2, 12.2);
    c.moveTo(12.2, 8.9);
    c.lineTo(11.8, 12.2);
    c.stroke();
  });
  const motorcycle = glyph(atlas, 'm-moto', 18, 18, 6, (c) => {
    c.beginPath();
    c.moveTo(9, 0.8);
    c.lineTo(17.2, 9);
    c.lineTo(9, 17.2);
    c.lineTo(0.8, 9);
    c.closePath();
    c.fillStyle = '#f3ecdb';
    c.fill();
    c.lineWidth = 1.1;
    c.strokeStyle = INK;
    c.stroke();
    c.lineWidth = 1.2;
    c.beginPath();
    c.arc(5.6, 10.6, 2.1, 0, Math.PI * 2);
    c.moveTo(14.5, 10.6);
    c.arc(12.4, 10.6, 2.1, 0, Math.PI * 2);
    c.stroke();
    c.beginPath();
    c.moveTo(5.6, 10.6);
    c.lineTo(8.6, 7.6);
    c.lineTo(11, 7.6);
    c.lineTo(12.4, 10.6);
    c.moveTo(10.6, 7.6);
    c.lineTo(10, 6);
    c.stroke();
  });
  const softDot = glyph(atlas, 'p-soft', 32, 32, 2, (c) => {
    const g = c.createRadialGradient(16, 16, 0, 16, 16, 16);
    g.addColorStop(0, 'rgba(255,255,255,0.9)');
    g.addColorStop(0.45, 'rgba(255,255,255,0.5)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    c.fillStyle = g;
    c.fillRect(0, 0, 32, 32);
  });
  const flash = glyph(atlas, 'p-flash', 32, 32, 2, (c) => {
    const g = c.createRadialGradient(16, 16, 0, 16, 16, 16);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.12, 'rgba(255,255,255,0.95)');
    g.addColorStop(0.3, 'rgba(255,255,255,0.35)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    c.fillStyle = g;
    c.fillRect(0, 0, 32, 32);
  });
  const rippleRing = glyph(atlas, 'p-ring', 64, 64, 2, (c) => {
    c.strokeStyle = '#fff';
    c.lineWidth = 2.2;
    c.beginPath();
    c.arc(32, 32, 29, 0, Math.PI * 2);
    c.stroke();
    c.lineWidth = 0.8;
    c.globalAlpha = 0.6;
    c.beginPath();
    c.arc(32, 32, 25.5, 0, Math.PI * 2);
    c.stroke();
  });
  const tooth = glyph(atlas, 'm-tooth', 8, 8, 6, (c) => {
    c.beginPath();
    c.moveTo(0.3, 7.7);
    c.lineTo(4, 0.6);
    c.lineTo(7.7, 7.7);
    c.closePath();
    c.fillStyle = '#fff';
    c.fill();
  });
  const chevron = glyph(atlas, 'm-chevron', 8, 8, 6, (c) => {
    c.beginPath();
    c.moveTo(7.4, 4);
    c.lineTo(0.8, 0.8);
    c.lineTo(2.6, 4);
    c.lineTo(0.8, 7.2);
    c.closePath();
    c.fillStyle = '#fff';
    c.fill();
  });
  // Selection rings are baked in full colour: brass alone vanishes on paper, so an ink
  // under-stroke and a pale highlight give contrast on any background.
  const ring = (key: string, main: string, light: string) =>
    glyph(atlas, key, 68, 52, 3, (c) => {
      c.lineJoin = 'round';
      c.beginPath();
      c.roundRect(4, 4, 60, 44, 4);
      c.shadowColor = 'rgba(20,14,6,0.55)';
      c.shadowBlur = 5;
      c.strokeStyle = 'rgba(33,26,18,0.85)';
      c.lineWidth = 3.8;
      c.stroke();
      c.shadowBlur = 0;
      c.strokeStyle = main;
      c.lineWidth = 2.4;
      c.stroke();
      c.strokeStyle = light;
      c.lineWidth = 0.7;
      c.stroke();
    });
  const selection = ring('m-select', '#c9a15c', '#f6e2ae');
  const inspect = ring('m-inspect', '#b3261e', '#f1b3a4');
  const hover = glyph(atlas, 'm-hover', 60, 44, 3, (c) => {
    c.beginPath();
    c.roundRect(3.5, 3.5, 53, 37, 3);
    c.strokeStyle = 'rgba(33,26,18,0.7)';
    c.lineWidth = 2.6;
    c.setLineDash([5, 3]);
    c.stroke();
    c.strokeStyle = '#fff';
    c.lineWidth = 1.3;
    c.stroke();
  });
  return {
    town,
    city,
    metropolis,
    capital,
    glow,
    sabres,
    rider,
    motorcycle,
    softDot,
    flash,
    ring: rippleRing,
    tooth,
    chevron,
    selection,
    inspect,
    hover,
  };
}

/** Monospaced tag characters (ghost ages etc.): ink on a paper halo. */
export function tagGlyphs(atlas: Atlas): Map<string, Glyph> {
  const map = new Map<string, Glyph>();
  for (const ch of '0123456789hd?+IVX') {
    map.set(
      ch,
      glyph(atlas, `tag-${ch}`, 7, 11, 5, (c) => {
        c.font = '600 9px "IBM Plex Mono", monospace';
        c.textAlign = 'center';
        c.textBaseline = 'middle';
        c.lineJoin = 'round';
        c.strokeStyle = 'rgba(243,236,219,0.95)';
        c.lineWidth = 2.6;
        c.strokeText(ch, 3.5, 5.8);
        c.fillStyle = INK;
        c.fillText(ch, 3.5, 5.8);
      }),
    );
  }
  return map;
}
